import { HTTP_CLIENT_KEY, IHTTPClient } from 'common/api/HTTPClient'
import { Container, IInit } from 'common/container/Container'
import { Observable, of } from 'rxjs'
import { MatchingContainerConfig } from '../container'
import { IStatusService } from '../../../common/status/StatusService'
import { STATUS_SERVICE_KEY } from '../../../container/app'
import { emptyList, ItemList } from '../../../common/models/ItemList'
import { Matching, MatchingDTO, MatchingQuery, toModel } from '../models/Matching'
import { Query } from '../../../common/api/Query'
import { catchError, map } from 'rxjs/operators'
import { prepareURL } from '../../../common/api/http-helpers'

export interface IMatchingApi extends IInit {
  getByID(id: string): Observable<Matching | undefined>
  getFilteredList(q: Query<MatchingQuery>): Observable<ItemList<Matching>>
  add(e: MatchingDTO): Observable<Matching | undefined>
  update(e: MatchingDTO): Observable<Matching | undefined>
  delete(id: string): Observable<boolean>
}

export class MatchingApi implements IMatchingApi {
  private _container!: Container
  private _httpClient!: IHTTPClient
  private _url!: string
  private _statusService!: IStatusService

  init(c: Container) {
    this._container = c
    this._httpClient = this._container.get<IHTTPClient>(HTTP_CLIENT_KEY)
    this._statusService = this._container.get<IStatusService>(STATUS_SERVICE_KEY)
    this._url = (this._container.config as MatchingContainerConfig).moduleFullUrl
  }

  getByID(id: string): Observable<Matching | undefined> {
    return this._httpClient.get<Matching>({ url: `${this._url}/${id}` }).pipe(
      map<MatchingDTO, Matching>((d) => toModel(d)),
      catchError((err) => {
        this._statusService.sendStatus({ variant: 'error', error: err })
        return of(undefined)
      })
    )
  }

  getFilteredList(q: Query<MatchingQuery>): Observable<ItemList<Matching>> {
    return this._httpClient.get<ItemList<Matching>>({ url: prepareURL(this._url, q) }).pipe(
      map<ItemList<MatchingDTO>, ItemList<Matching>>((dto) => {
        const itemList = emptyList<Matching>()
        itemList.count = dto.count
        itemList.items = dto.items.map((d) => toModel(d))
        return itemList
      }),
      catchError((err) => {
        this._statusService.sendStatus({ variant: 'error', error: err })
        return of(emptyList<Matching>())
      })
    )
  }

  add(e: MatchingDTO): Observable<Matching | undefined> {
    return this._httpClient.post<Matching>({ url: this._url, body: e }).pipe(
      catchError((err) => {
        this._statusService.sendStatus({ variant: 'error', error: err })
        return of(undefined)
      })
    )
  }

  update(e: MatchingDTO): Observable<Matching | undefined> {
    return this._httpClient.put<Matching>({ url: this._url, body: e }).pipe(
      map<MatchingDTO, Matching>((d) => toModel(d)),
      catchError((err) => {
        this._statusService.sendStatus({ variant: 'error', error: err })
        return of(undefined)
      })
    )
  }

  delete(id: string): Observable<boolean> {
    return this._httpClient.delete({ url: `${this._url}/${id}` }).pipe(
      catchError((err) => {
        this._statusService.sendStatus({ variant: 'error', error: err })
        return of(false)
      })
    )
  }
}
